import { getCommonAxiosCommon, UrlBuilder } from 'app/helpers/MiscUtils';
import { NgAPIResponse } from 'app/types';
import axios from 'axios';
import { config } from 'config';

import { UpdateVJFPUserInput, UserStatus, VJFPUser, VJFPUserGroup } from '../API';
import { VJFPCache } from './CacheUtils';

const EndPoint = {
  vjfpUsers: `${config.vjfpNGRestAPI}/vjfpUsers/:organizationId`,
  vjfpUserById: `${config.vjfpNGRestAPI}/vjfpUsers/:organizationId/:id`,
  vjfpUserByEmailId: `${config.vjfpNGRestAPI}/vjfpUsers/byEmailId/:emailId`,
  vjfpUserByMultipleIds: `${config.vjfpNGRestAPI}/vjfpUsers/:organizationId?userIds=`,
  vjfpUserGroups: `${config.vjfpNGRestAPI}/vjfpUsers/:organizationId/groups`,
  vjfpUserGroupById: `${config.vjfpNGRestAPI}/vjfpUsers/:organizationId/groups/:groupId`,
  vjfpUsersByGroup: `${config.vjfpNGRestAPI}/vjfpUsers/:organizationId/groups/:groupId/users`,
  vjfpAddUserToGroup: `${config.vjfpNGRestAPI}/vjfpUsers/:organizationId/:id/groups/:groupId`
};

export class VJFPUserService {
  async createOrgUser (organizationId: string, user: Partial<VJFPUser>, tempPassword: string): Promise<number> {
    const result = await axios.post<{ userId: number }>(UrlBuilder(EndPoint.vjfpUsers, { organizationId }), { ...user, tempPassword }, await getCommonAxiosCommon());
    return result.data.userId;
  }

  // Get Users by organizationId
  async getUsers (organizationId: string): Promise<VJFPUser[]> {
    try {
      const result = await axios.get<NgAPIResponse<VJFPUser[]>>(
        UrlBuilder(EndPoint.vjfpUsers, { organizationId }),
        await getCommonAxiosCommon()
      );
      if (!result.data?.isSuccess) {
        console.log(result.data?.errors);
      }
      return result.data?.data || [];
    } catch (err) {
      console.log(err);
      return [];
    }
  }

  async getById (organizationId: string, userId: number): Promise<VJFPUser | undefined> {
    const lastCachedUserInfo: VJFPUser = VJFPCache.getItem('vjfpUser-' + userId) as VJFPUser;
    if (lastCachedUserInfo?.presenceStatus) {
      return Promise.resolve(lastCachedUserInfo);
    }

    try {
      const result = await axios.get<NgAPIResponse<VJFPUser>>(
        UrlBuilder(EndPoint.vjfpUserById, { organizationId, id: userId.toString() }),
        await getCommonAxiosCommon()
      );
      if (
        result?.data.data?.presenceStatus &&
        (lastCachedUserInfo?.presenceStatus !== result?.data.data?.presenceStatus ||
          lastCachedUserInfo.isAvailableForChat !== result?.data.data.isAvailableForChat)
      ) {
        VJFPCache.setItem('vjfpUser-' + userId, result?.data);
      }
      if (!result.data?.isSuccess) {
        console.log(result.data?.errors);
      }
      return result.data?.data as VJFPUser;
    } catch (err) {
      console.log(err);
    }
  }

  async getByEmail (emailId: string): Promise<VJFPUser | undefined> {
    try {
      const result = await axios.get<NgAPIResponse<VJFPUser>>(
        UrlBuilder(EndPoint.vjfpUserByEmailId, { emailId }),
        await getCommonAxiosCommon()
      );
      if (result?.data.data?.presenceStatus) {
        VJFPCache.setItem('vjfpUser-' + result?.data.data?.id, result?.data);
      }
      if (!result.data?.isSuccess) {
        console.log(result.data?.errors);
      }
      return result.data?.data as VJFPUser;
    } catch (err) {
      console.log(err);
    }
  }

  async getByIds (organizationId: string, userIds: number[]): Promise<VJFPUser[] | undefined> {
    if (userIds?.length === 1) {
      const vjfpUser = await this.getById(organizationId, userIds[0]);
      if (vjfpUser) return [vjfpUser];
      return [];
    }
    try {
      const lastCachedMap = new Map<number, VJFPUser>();
      userIds.forEach((u) => {
        const lastCachedUserInfo = VJFPCache.getItem('vjfpUser-' + u);
        if (lastCachedUserInfo?.presenceStatus) {
          lastCachedMap.set(u, lastCachedUserInfo as VJFPUser);
        }
      });
      const _userIdsNotInCache = userIds.filter((x) => !lastCachedMap.has(x));
      if (_userIdsNotInCache.length === 0) {
        return Array.from(lastCachedMap, ([key, value]) => value);
      }
      const notCachedUsersResult = await axios.get<NgAPIResponse<VJFPUser[]>>(
        `${UrlBuilder(EndPoint.vjfpUsers, { organizationId })}?ids=${_userIdsNotInCache.join(',')}`,
        await getCommonAxiosCommon()
      );
      notCachedUsersResult.data.data.forEach((_vjfpUser) => {
        VJFPCache.setItem('vjfpUser-' + _vjfpUser.id, _vjfpUser);
      });

      if (!notCachedUsersResult.data?.isSuccess) {
        console.log(notCachedUsersResult.data?.errors);
      }
      const cachedUsers = Array.from(lastCachedMap, ([key, value]) => value);
      return [...notCachedUsersResult.data.data, ...cachedUsers];
    } catch (err) {
      console.log(err);
    }
  }

  async updateAvailability (jobFairId: string, organizationId: string, id: number, isAvailableForChat: boolean) {
    try {
      const axiosConfig = await getCommonAxiosCommon();

      const result = await axios.put<NgAPIResponse<VJFPUser>>(
        UrlBuilder(EndPoint.vjfpUserById, { organizationId, id: id.toString() }),
        {
          id,
          isAvailableForChat,
          presenceStatus: UserStatus.LOGGED_IN,
          lastPresenceJobFairId: jobFairId
        },
        axiosConfig
      );
      if (!result.data?.isSuccess) {
        console.log(result.data?.errors);
      }

      return result?.data.data;
    } catch (err) {
      console.log(err);
    } finally {
      VJFPCache.removeItem('vjfpUser-' + id);
    }
  }

  async updateProfile (organizationId: string, id: number, input: Partial<UpdateVJFPUserInput>) {
    try {
      const axiosConfig = await getCommonAxiosCommon();
      const result = await axios.put<NgAPIResponse<VJFPUser>>(
        UrlBuilder(EndPoint.vjfpUserById, { organizationId, id: id.toString() }),
        { ...input, id },
        axiosConfig
      );
      if (!result.data?.isSuccess) {
        console.log(result.data?.errors);
      }
      return result?.data?.data;
    } catch (err) {
      console.log(err);
    }
  }

  async updateStatus (organizationId: string, id: number, presenceStatus: UserStatus) {
    try {
      const input: UpdateVJFPUserInput = {
        id,
        presenceStatus: presenceStatus
      };

      if (input.presenceStatus === UserStatus.LOGGED_OUT) {
        input.isAvailableForChat = true;
      }

      const axiosConfig = await getCommonAxiosCommon();
      const result = await axios.put<NgAPIResponse<VJFPUser>>(
        UrlBuilder(EndPoint.vjfpUserById, { organizationId, id: id.toString() }),
        input,
        axiosConfig
      );
      if (!result.data?.isSuccess) {
        console.log(result.data?.errors);
      }
      return result?.data?.data;
    } catch (err) {
      console.log(err);
    } finally {
      VJFPCache.removeItem('vjfpUser-' + id);
    }
  }

  async listUsersByGroup (organizationId: string, groupId: string): Promise<VJFPUser[]> {
    try {
      const result = await axios.get<NgAPIResponse<VJFPUser[]>>(
        UrlBuilder(EndPoint.vjfpUsersByGroup, { organizationId, groupId }),
        await getCommonAxiosCommon()
      );
      if (!result.data?.isSuccess) {
        console.log(result.data?.errors);
      }
      return result.data?.data || [];
    } catch (err) {
      console.log(err);
      return [];
    }
  }

  async createGroup (organizationId: string, groupData: any): Promise<string> {
    const result = await axios.post<NgAPIResponse<{ groupId: string }>>(
      UrlBuilder(EndPoint.vjfpUserGroups, { organizationId }),
      groupData,
      await getCommonAxiosCommon()
    );
    return result.data.data.groupId;
  }

  async listGroups (organizationId: string): Promise<VJFPUserGroup[]> {
    try {
      const result = await axios.get<NgAPIResponse<VJFPUserGroup[]>>(
        UrlBuilder(EndPoint.vjfpUserGroups, { organizationId }),
        await getCommonAxiosCommon()
      );
      if (!result.data?.isSuccess) {
        console.log(result.data?.errors);
      }
      return result.data?.data || [];
    } catch (err) {
      console.log(err);
      return [];
    }
  }

  async deleteGroup (organizationId: string, groupId: string): Promise<void> {
    try {
      const result = await axios.delete<NgAPIResponse<any>>(
        UrlBuilder(EndPoint.vjfpUserGroupById, { organizationId, groupId }),
        await getCommonAxiosCommon()
      );
      if (!result.data?.isSuccess) {
        console.log(result.data?.errors);
      }
    } catch (err) {
      console.log(err);
    }
  }

  async addUserToGroup (organizationId: string, userId: number, groupId: string): Promise<void> {
    try {
      const result = await axios.post<NgAPIResponse<any>>(
        UrlBuilder(EndPoint.vjfpAddUserToGroup, { organizationId, id: userId.toString(), groupId }),
        {},
        await getCommonAxiosCommon()
      );
      if (!result.data?.isSuccess) {
        console.log(result.data?.errors);
      }
    } catch (err) {
      console.log(err);
    }
  }

  async getUserExistsWithRole (emailId: string): Promise<{exists: boolean, roles?: string}> {
    const result = await axios.get<NgAPIResponse<{exists: boolean, roles?: string}>>(
        `${config.vjfpNGRestAPI}/public/check-user-exists/${emailId}`
    );
    return result?.data?.data;
  }
}
